/** CT-301 Verify Card: the code pbx-api, finance and sip.io send when a charge hits an unverified card. */
export const CARD_UNVERIFIED = "card_unverified";

type ErrorBody = { error?: unknown; detail?: unknown; reason?: unknown };

/** True for any refusal caused by an unverified card. Plan changes wrap finance's code in `detail`. */
export function isCardUnverifiedError(err: unknown): boolean {
    const data = (err as { response?: { data?: ErrorBody } })?.response?.data;
    if (!data || typeof data !== "object") return false;

    return data.error === CARD_UNVERIFIED || data.detail === CARD_UNVERIFIED || data.reason === CARD_UNVERIFIED;
}

/** Same rule as pbx-api's parser: dollars and up to 2 decimals, e.g. "0.57" or ".57", under $1.00 like every hold. */
const HOLD_AMOUNT = /^\d{0,2}(\.\d{1,2})?$/;
/** Any dollars-and-cents number, so a refusal can say which part was wrong. */
const MONEY = /^\d*(\.\d{1,2})?$/;

export type HoldAmountProblem = "empty" | "format" | "range";

/** The fields take 0.75, .75 or $0.75 (AC 12); pbx-api's parser takes no $, so it comes off before sending. */
export function normalizeHoldAmount(value: string): string {
    return value.trim().replace(/^\$/, "");
}

/** Why a typed hold amount can't be sent (CT-756 QA #1), or null when it can. */
export function holdAmountProblem(value: string): HoldAmountProblem | null {
    const normalized = normalizeHoldAmount(value);
    if (normalized === "") return "empty";
    if (!/\d/.test(normalized) || !MONEY.test(normalized)) return "format";
    const amount = Number(normalized);

    return HOLD_AMOUNT.test(normalized) && amount > 0 && amount < 1 ? null : "range";
}

/** CT-756: the API refused a card setup because the account is locked out of card verification. */
export function isVerificationLocked(err: unknown): boolean {
    return (err as { response?: { data?: ErrorBody } })?.response?.data?.error === "card_verification_locked";
}

/** CT-756: support for a lock-out by email, which works on every page. The dashboard's Contact Support opens the AI
 *  assistant, which the add-card wall, signup, the inbox and the closed page do not mount. */
export function lockoutSupportHref(subject: string): string {
    return `mailto:support@pbx.im?subject=${encodeURIComponent(subject)}`;
}
